// components/auth/AuthInitializer.tsx

import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { useAuth } from '../../auth/hooks/useAuth';
import { useUserProfile } from '../../hooks/useUserProfile';

import { updateUserProfilePicture } from '../../store/slices/userProfile/userProfileSlice';

/**
 * 🔐 AuthInitializer
 *
 * Responsabilidades:
 * - restaurar datos globales del usuario
 * - cargar perfil una sola vez
 * - cargar foto de perfil una sola vez
 * - hidratar Redux global
 *
 * IMPORTANTE:
 * DropdownUser NO debe hacer fetch.
 * Solo debe leer Redux.
 */
const AuthInitializer = () => {
  const dispatch = useDispatch();

  const { auth, getAuthenticatedUserEmail } = useAuth();

  const { fetchUserProfile, userProfile, handleGetProfilePicture } =
    useUserProfile();

  const isAuth = !!auth?.accessToken && auth?.isAuthenticated;

  // =========================================================
  // 👤 Cargar perfil base
  // =========================================================

  useEffect(() => {
    if (!isAuth) return;

    const email = getAuthenticatedUserEmail();

    if (!email) return;

    fetchUserProfile(email);
  }, [isAuth]);

  // =========================================================
  // 🖼️ Cargar foto perfil una sola vez
  // =========================================================

  useEffect(() => {
    const loadProfilePicture = async () => {
      const userId = userProfile?.data?.id;

      if (!userId) return;

      // 🔥 Revisar cache local primero
      const storedImage = sessionStorage.getItem(`profileImage-${userId}`);

      if (storedImage) {
        dispatch(updateUserProfilePicture(storedImage));
        return;
      }

      // 🟢 Backend fallback
      const image = await handleGetProfilePicture(userId);

      if (!image) {
        dispatch(updateUserProfilePicture(null));
        return;
      }

      dispatch(updateUserProfilePicture(image));
    };

    loadProfilePicture();
  }, [userProfile?.data?.id]);

  return null;
};

export default AuthInitializer;
